import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./strikingforcemarkup";
import { usePage } from "../hooks/usePage";

import {
    fetchStrikingForces,
    fetchStrikingForceById,
    createStrikingForce,
    editStrikingForce,
    clearStrikingForceError,
} from "../store/slices/strikingforce";

import { getOfficers } from "../store/slices/user";
import { getBooths } from "../store/slices/booth";

const PageMarkup = memo(Markup);

const initialForm = {
    name: "",
    dspId: "",
    users: [],
    boothIds: [],
};

const getId = (value) => {
    if (!value) return "";
    return typeof value === "object" ? value._id || "" : value;
};

const normalizeStrikingForce = (data) => {
    if (!data) return null;

    return (
        data.strikingForce ||
        data.data?.strikingForce ||
        data.data ||
        data
    );
};

export default function StrikingForces() {
    usePage({
        title: "Striking Force Management - Nellore Police Dashboard",
        bodyClass,
    });

    const dispatch = useDispatch();

    const {
        strikingForces = [],
        loading,
        submitting,
        error,
    } = useSelector((state) => state.strikingForce);

    const { officers = [] } = useSelector((state) => state.user);
    const { booths = [] } = useSelector((state) => state.booth);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("ALL");
    const [viewMode, setViewMode] = useState("grid");

    const [showModal, setShowModal] = useState(false);
    const [editingStrikingForceId, setEditingStrikingForceId] =
        useState(null);

    const [detailsLoading, setDetailsLoading] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState("");

    // --------------------------------------------------
    // INITIAL DATA
    // --------------------------------------------------

    const loadData = () => {
        dispatch(fetchStrikingForces());

        dispatch(
            getOfficers({
                active: true,
                page: 1,
                limit: 1000,
            })
        );

        dispatch(
            getBooths({
                page: 1,
                limit: 1000,
            })
        );
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // --------------------------------------------------
    // FILTER DATA
    // --------------------------------------------------

    const filteredStrikingForces = useMemo(() => {
        return strikingForces.filter((item) => {
            const searchText = search.trim().toLowerCase();

            const dspName =
                item.dspId?.displayName ||
                item.dspId?.name ||
                "";

            const matchesSearch =
                !searchText ||
                [
                    item.name,
                    dspName,
                    item.dspId?.officerId,
                ].some((value) =>
                    String(value || "")
                        .toLowerCase()
                        .includes(searchText)
                );

            const matchesStatus =
                status === "ALL" ||
                String(item.status || "ACTIVE").toUpperCase() === status;

            return matchesSearch && matchesStatus;
        });
    }, [strikingForces, search, status]);

    // --------------------------------------------------
    // STATISTICS
    // --------------------------------------------------

    const statistics = useMemo(
        () => ({
            total: strikingForces.length,

            active: strikingForces.filter(
                (item) =>
                    String(item.status || "ACTIVE").toUpperCase() ===
                    "ACTIVE"
            ).length,

            inactive: strikingForces.filter(
                (item) =>
                    String(item.status || "").toUpperCase() ===
                    "INACTIVE"
            ).length,

            totalUsers: strikingForces.reduce(
                (total, item) => total + (item.users?.length || 0),
                0
            ),

            totalBooths: strikingForces.reduce(
                (total, item) => total + (item.boothIds?.length || 0),
                0
            ),
        }),
        [strikingForces]
    );

    // --------------------------------------------------
    // MODAL
    // --------------------------------------------------

    const openAddModal = () => {
        dispatch(clearStrikingForceError());

        setEditingStrikingForceId(null);
        setForm(initialForm);
        setFormError("");
        setShowModal(true);
    };

    const openEditModal = async (strikingForce) => {
        dispatch(clearStrikingForceError());

        setEditingStrikingForceId(strikingForce._id);
        setForm(initialForm);
        setFormError("");
        setShowModal(true);
        setDetailsLoading(true);

        try {
            const response = await dispatch(
                fetchStrikingForceById(strikingForce._id)
            ).unwrap();

            const selectedStrikingForce =
                normalizeStrikingForce(response);

            if (!selectedStrikingForce?._id) {
                throw new Error("Striking Force details not found");
            }

            setForm({
                name: selectedStrikingForce.name || "",
                dspId: getId(selectedStrikingForce.dspId),

                users: Array.isArray(selectedStrikingForce.users)
                    ? selectedStrikingForce.users
                        .map((item) => getId(item))
                        .filter(Boolean)
                    : [],

                boothIds: Array.isArray(selectedStrikingForce.boothIds)
                    ? selectedStrikingForce.boothIds
                        .map((item) => getId(item))
                        .filter(Boolean)
                    : [],
            });
        } catch (err) {
            console.error("Striking Force edit error:", err);

            setFormError(
                typeof err === "string"
                    ? err
                    : err?.message || "Failed to load Striking Force details"
            );
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeModal = () => {
        if (submitting) return;

        setShowModal(false);
        setEditingStrikingForceId(null);
        setForm(initialForm);
        setFormError("");
    };

    // --------------------------------------------------
    // FORM HANDLERS
    // --------------------------------------------------

    const handleFormChange = (field, value) => {
        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleUserToggle = (userId, checked) => {
        setForm((previous) => ({
            ...previous,
            users: checked
                ? [...new Set([...previous.users, userId])]
                : previous.users.filter((id) => id !== userId),
        }));
    };

    const handleBoothToggle = (boothId, checked) => {
        setForm((previous) => ({
            ...previous,
            boothIds: checked
                ? [...new Set([...previous.boothIds, boothId])]
                : previous.boothIds.filter((id) => id !== boothId),
        }));
    };

    // --------------------------------------------------
    // CREATE / UPDATE
    // --------------------------------------------------

    const handleSubmit = async (event) => {
        event?.preventDefault();

        setFormError("");

        if (!form.name.trim()) {
            setFormError("Please enter Striking Force name");
            return;
        }

        if (!form.dspId) {
            setFormError("Please select DSP in-charge");
            return;
        }

        const payload = {
            name: form.name.trim(),
            dspId: form.dspId,
            users: form.users,
            boothIds: form.boothIds,
        };

        try {
            if (editingStrikingForceId) {
                await dispatch(
                    editStrikingForce({
                        id: editingStrikingForceId,
                        payload,
                    })
                ).unwrap();
            } else {
                await dispatch(
                    createStrikingForce(payload)
                ).unwrap();
            }

            setShowModal(false);
            setEditingStrikingForceId(null);
            setForm(initialForm);
            setFormError("");

            dispatch(fetchStrikingForces());
        } catch (err) {
            setFormError(
                typeof err === "string"
                    ? err
                    : err?.message || "Failed to save Striking Force"
            );
        }
    };

    // --------------------------------------------------
    // RENDER
    // --------------------------------------------------

    return (
        <PageMarkup
            strikingForces={filteredStrikingForces}
            totalStrikingForces={strikingForces.length}
            statistics={statistics}
            loading={loading}
            submitting={submitting}
            detailsLoading={detailsLoading}
            error={error}
            search={search}
            setSearch={setSearch}
            status={status}
            setStatus={setStatus}
            viewMode={viewMode}
            setViewMode={setViewMode}
            showModal={showModal}
            editingStrikingForceId={editingStrikingForceId}
            form={form}
            formError={formError}
            officers={officers}
            booths={booths}
            onAddStrikingForce={openAddModal}
            onEditStrikingForce={openEditModal}
            onCloseModal={closeModal}
            onFormChange={handleFormChange}
            onUserToggle={handleUserToggle}
            onBoothToggle={handleBoothToggle}
            onSubmit={handleSubmit}
        />
    );
}